import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { useCallback, useEffect, useState } from 'react';

// URL에서 이미지 크기를 가져오는 유틸리티 함수
const getImageDimensions = url => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      reject(new Error(`Failed to load image: ${url}`));
    };
    img.src = url;
  });
};

export default function ImageViewer({ images, galleryID }) {
  const [processedImages, setProcessedImages] = useState([]);

  // 이미지들이 크기 정보를 가지도록 처리
  const processImages = useCallback(async () => {
    const processed = await Promise.all(
      images.map(async (image, index) => {
        // 이미 크기가 제공된 경우, 그것을 사용
        if (image.width && image.height) {
          return image;
        }

        // 그렇지 않으면, 자동으로 크기 감지
        try {
          const dimensions = await getImageDimensions(
            image.largeURL || image.thumbnailURL
          );
          return {
            ...image,
            width: dimensions.width,
            height: dimensions.height,
          };
        } catch (error) {
          console.warn(`Failed to get dimensions for image ${index}:`, error);
          // 기본 크기로 폴백
          return {
            ...image,
            width: 800,
            height: 600,
          };
        }
      })
    );
    setProcessedImages(processed);
  }, [images]);

  // 컴포넌트가 마운트되거나 이미지가 변경될 때 이미지 처리
  useEffect(() => {
    processImages();
  }, [processImages]);

  useEffect(() => {
    // 이미지가 처리될 때까지 PhotoSwipe 초기화하지 않음
    if (processedImages.length === 0) return;

    let lightbox = new PhotoSwipeLightbox({
      gallery: '#' + galleryID,
      children: 'a',
      pswpModule: () => import('photoswipe'),
      wheelToZoom: true,
    });

    // PhotoSwipe 문서에 따라 커스텀 다운로드 버튼 등록
    lightbox.on('uiRegister', function () {
      lightbox.pswp.ui.registerElement({
        name: 'download-button',
        order: 8, // 카운터와 닫기 버튼 사이
        isButton: true,
        tagName: 'a',
        html: '<svg width="32" height="32" viewBox="0 0 32 32" aria-hidden="true" class="pswp__icn"><path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" /></svg>',
        onInit: (el, pswp) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');
          pswp.on('change', () => {
            el.href = pswp.currSlide.data.src;
          });
        },
      });
      // 나만의 카드뉴스 다운로드 버튼
      lightbox.pswp.ui.registerElement({
        name: 'secondary-download-button',
        order: 999, // 끝에 위치
        isButton: true,
        tagName: 'a',
        html: '나만의 카드뉴스 다운로드',
        onInit: (el, pswp) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');

          // 버튼을 가로 중앙, 세로 하단에 위치
          el.style.position = 'fixed';
          el.style.left = '50%';
          el.style.transform = 'translateX(-50%)';
          el.style.bottom = '80px';
          el.style.zIndex = '4000';
          el.style.backgroundColor = '#4E2BF4';
          el.style.color = 'white';
          el.style.borderRadius = '8px';
          el.style.padding = '12px 24px';
          el.style.border = 'none';
          el.style.cursor = 'pointer';
          el.style.fontSize = '14px';
          el.style.fontWeight = '500';
          el.style.textDecoration = 'none';
          el.style.display = 'inline-block';
          el.style.transition =
            'background-color 0.2s ease, transform 0.2s ease';
          el.style.textAlign = 'center';
          el.style.minWidth = 'max-content';
          el.style.maxWidth = '300px';
          el.style.height = '48px';

          // 호버 효과
          el.addEventListener('mouseenter', () => {
            el.style.backgroundColor = '#4229B6'; // 호버 시 약간 밝은 파란색
            el.style.transform = 'translateX(-50%) translateY(-2px)';
          });

          el.addEventListener('mouseleave', () => {
            el.style.backgroundColor = '#4E2BF4'; // primary.darkBlue
            el.style.transform = 'translateX(-50%) translateY(0)';
          });

          pswp.on('change', () => {
            el.href = pswp.currSlide.data.src;
          });
        },
      });
    });

    lightbox.init();

    return () => {
      lightbox.destroy();
      lightbox = null;
    };
  }, [galleryID, processedImages]);

  return (
    <div className="pswp-gallery" id={galleryID}>
      {processedImages.map((image, index) => (
        <a
          href={image.largeURL}
          data-pswp-width={image.width}
          data-pswp-height={image.height}
          key={galleryID + '-' + index}
          target="_blank"
          rel="noreferrer"
        >
          <img src={image.thumbnailURL} alt="" />
        </a>
      ))}
    </div>
  );
}
